import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const UPDATED = '2026-10-21';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Looking for a UK turmeric supplement you can buy today?</strong>
      <span className="block text-emerald-800/80 mt-0.5">IIY Phytoventra BCM-95® — patented Curcugreen® turmeric extract, piperine-free, vegan. £44.99 (first bottle £37.99), UK VAT &amp; delivery included.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors" data-testid="hub-inline-cta">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const GuideLink = ({ to, children }) => (
  <Link to={to} className="text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-2 hover:decoration-emerald-600">{children}</Link>
);

const sections = [
  {
    id: 'quick-answer',
    heading: 'Turmeric supplements UK — the short answer',
    body: (
      <>
        <p>A turmeric supplement is a food supplement that concentrates the active compounds of the turmeric root — the <strong>curcuminoids</strong>, of which <strong>curcumin</strong> is the main one — into capsules or tablets. In the UK they are sold as food supplements, not medicines, so the right way to compare them is by <strong>how much curcumin each serving delivers</strong> and <strong>how the product solves curcumin&rsquo;s poor absorption</strong> — not by the total &ldquo;turmeric&rdquo; milligrams on the front of the pack.</p>
        <p>If you want a clean-label, UK-bought option, <strong>IIY Phytoventra BCM-95<sup>&reg;</sup></strong> is a curcumin food supplement built on the patented <strong>BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup></strong> extract. It is <strong>piperine-free</strong> (it uses turmeric&rsquo;s own essential oils for absorption), delivers <strong>650&nbsp;mg of curcumin per 2-capsule serving</strong> (from 1000&nbsp;mg of purified turmeric extract), and comes in 60 vegan capsules — a 30-day supply.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'turmeric-vs-curcumin',
    heading: 'Turmeric vs curcumin: what you\u2019re actually buying',
    body: (
      <>
        <p>Turmeric is the golden spice; curcumin (and the related curcuminoids) are the studied compounds inside it. Raw culinary turmeric is only around <strong>2–5% curcuminoids by weight</strong>, which is why standardised extracts exist — they concentrate the curcumin so you don&rsquo;t need to eat spoonfuls of spice. When you read a label, look for the stated <strong>curcumin content</strong>, not just &ldquo;turmeric 500&nbsp;mg&rdquo;.</p>
        <p>For a plain-English breakdown, see our guide <GuideLink to="/curcumin-vs-turmeric">Curcumin vs Turmeric</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'types',
    heading: 'The main types of turmeric supplement sold in the UK',
    body: (
      <>
        <p>Because curcumin on its own is poorly absorbed, nearly every serious curcumin supplement uses an <strong>absorption strategy</strong>. The common families are:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Turmeric-oil formulations</strong> (e.g. BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>) — curcumin recombined with turmeric&rsquo;s own essential oils (turmerones); 100% from the turmeric plant, no black pepper.</li>
          <li><strong>Piperine / black-pepper</strong> formulas — add black-pepper extract to slow curcumin breakdown (note: piperine can interact with some medicines).</li>
          <li><strong>Phospholipid complexes</strong> (e.g. Meriva<sup>&reg;</sup>) — curcumin bound to lecithin; often lower curcumin per capsule and may contain soy.</li>
          <li><strong>Colloidal / lipid-matrix</strong> systems (e.g. Longvida<sup>&reg;</sup>) — curcumin protected for gut absorption.</li>
          <li><strong>Plain 95% curcumin extract</strong> — no enhancer; absorbed and excreted quickly.</li>
        </ul>
        <p>We compare these side by side in <GuideLink to="/best-curcumin-supplement-uk">Best Curcumin Supplement UK</GuideLink>, and explain the turmeric-oil approach in <GuideLink to="/bcm-95-explained">BCM-95® Explained</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'what-to-check',
    heading: 'What to check before you buy',
    body: (
      <>
        <ol className="list-decimal pl-6 space-y-2">
          <li><strong>Curcumin per serving</strong> — a real number (e.g. 650&nbsp;mg), not just total turmeric weight.</li>
          <li><strong>A named, studied extract</strong> — BCM-95<sup>&reg;</sup>/Curcugreen<sup>&reg;</sup>, Meriva<sup>&reg;</sup> and Longvida<sup>&reg;</sup> all have published pharmacokinetic data.</li>
          <li><strong>How absorption is achieved</strong> — the label should say (turmeric oils, piperine, phospholipid, colloidal).</li>
          <li><strong>Clean label &amp; allergens</strong> — vegan capsules, allergen declarations, no unnecessary excipients. See <GuideLink to="/vegan-turmeric-supplement-uk">Vegan Turmeric Supplement UK</GuideLink>.</li>
          <li><strong>UK consumer protections</strong> — a registered UK food business, a real address, a VAT receipt and a clear returns policy.</li>
        </ol>
        <p>Step-by-step help: <GuideLink to="/how-to-choose-a-turmeric-supplement">How to Choose a Turmeric Supplement</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'why-bcm95',
    heading: 'Why UK buyers pick IIY Phytoventra BCM-95®',
    body: (
      <>
        <ul className="not-prose space-y-2.5 my-2">
          {[
            'Patented BCM-95® / Curcugreen® extract — absorption from turmeric&rsquo;s own oils, no piperine.',
            'Published pharmacokinetic data shows up to 7× greater absorption than standard curcumin (Antony et al., 2008).',
            '650 mg curcumin per 2-capsule serving (from 1000 mg purified turmeric extract).',
            '60 vegan capsules — a 30-day supply; allergen-free; made under GMP using 100% solar power.',
            'Bought directly from a UK company (IIY Phytoventra Ltd, Northampton) with a VAT invoice and 14-day returns.',
            'All-inclusive pricing: £44.99 (first bottle £37.99), UK VAT and free Royal Mail 48 delivery included.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-slate-700">
              <Check className="w-4 h-4 mt-1 shrink-0 text-emerald-600" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Turmeric supplements UK — frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">What is the best turmeric supplement in the UK?</p>
        <p>There is no single &ldquo;best&rdquo; for everyone — it depends on whether you want a piperine-free formula, a specific curcumin dose or a particular allergen profile. Among clean-label, piperine-free options, IIY Phytoventra BCM-95<sup>&reg;</sup> is a strong UK choice: a patented turmeric-oil extract delivering 650&nbsp;mg curcumin per serving in vegan capsules, sold directly by a UK company with a VAT receipt and free delivery.</p>
        <p className="font-semibold text-emerald-900 mt-4">Are turmeric and curcumin supplements the same thing?</p>
        <p>Not quite. Turmeric is the root/spice; curcumin is the main active compound inside it (around 2–5% of raw turmeric). A good curcumin supplement states the curcumin content per serving rather than just the total turmeric weight.</p>
        <p className="font-semibold text-emerald-900 mt-4">Do turmeric supplements need black pepper?</p>
        <p>No. Black pepper (piperine) is one way to aid absorption, but it is not the only one. BCM-95<sup>&reg;</sup> uses turmeric&rsquo;s own essential oils instead, so it is completely piperine-free — useful if you prefer to avoid black-pepper extract.</p>
        <p className="font-semibold text-emerald-900 mt-4">Where can I buy a turmeric supplement online in the UK?</p>
        <p>You can buy IIY Phytoventra BCM-95<sup>&reg;</sup> directly at iiypv.com; it ships from the UK by Royal Mail 48 tracked delivery, with UK VAT and delivery already included in the price.</p>
      </>
    ),
  },
];

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { q: 'What is the best turmeric supplement in the UK?', a: 'It depends on whether you want a piperine-free formula, a specific curcumin dose or a particular allergen profile. Among clean-label, piperine-free options, IIY Phytoventra BCM-95® is a strong UK choice: a patented turmeric-oil extract delivering 650 mg curcumin per serving in vegan capsules, sold directly by a UK company with a VAT receipt and free delivery.' },
    { q: 'Are turmeric and curcumin supplements the same thing?', a: 'Not quite. Turmeric is the root/spice; curcumin is the main active compound inside it (around 2–5% of raw turmeric). A good curcumin supplement states the curcumin content per serving rather than just the total turmeric weight.' },
    { q: 'Do turmeric supplements need black pepper?', a: 'No. Black pepper (piperine) is one way to aid absorption, but it is not the only one. BCM-95® uses turmeric\u2019s own essential oils instead, so it is completely piperine-free.' },
    { q: 'Where can I buy a turmeric supplement online in the UK?', a: 'You can buy IIY Phytoventra BCM-95® directly at iiypv.com; it ships from the UK by Royal Mail 48 tracked delivery, with UK VAT and delivery already included in the price.' },
  ].map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function TurmericSupplementsUK() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="Turmeric Supplements UK: How to Choose + Best Options | IIY"
        description="A UK buyer's guide to turmeric & curcumin supplements — types, absorption, how much curcumin per serving, vegan & piperine-free options, and where to buy online."
        keywords="turmeric supplement UK, turmeric capsules UK, curcumin supplement UK, best turmeric supplement UK, high strength turmeric, turmeric 1000mg, piperine-free turmeric, vegan turmeric supplement, high absorption curcumin, BCM-95, Curcugreen"
        pathOverride="/turmeric-supplements-uk"
        heading="Turmeric Supplements UK: A Plain-English Buyer’s Guide"
        subheading="What a turmeric supplement actually is, how the UK's main formulations differ, how much curcumin to look for, and where a clean-label, piperine-free option fits."
        tag="Turmeric Supplements UK"
        datePublished={UPDATED}
        dateModified={UPDATED}
        sections={sections}
      />
    </>
  );
}
